import React, { useState, useMemo } from 'react';
import { discographyData, getAllGenres, getAllRoles, getAllPlatforms } from '../data/discography.js';
import './Discography.css';

const Discography = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [sortField, setSortField] = useState('releaseDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedSong, setSelectedSong] = useState(null);

  const allGenres = getAllGenres();
  const allRoles = getAllRoles();
  const allPlatforms = getAllPlatforms();

  // Filter and sort songs
  const filteredSongs = useMemo(() => {
    let filtered = discographyData.filter(song => {
      const matchesSearch = searchTerm === '' || 
        song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        song.artist.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (song.album && song.album.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesGenres = selectedGenres.length === 0 || 
        selectedGenres.some(genre => song.genre.includes(genre));

      const matchesRoles = selectedRoles.length === 0 || 
        selectedRoles.some(role => song.roles.includes(role));

      const matchesPlatforms = selectedPlatforms.length === 0 || 
        selectedPlatforms.some(platform => song.links.some(link => link.platform === platform));

      return matchesSearch && matchesGenres && matchesRoles && matchesPlatforms;
    });

    // Sort songs
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === 'releaseDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else {
        aValue = aValue?.toLowerCase() || '';
        bValue = bValue?.toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [searchTerm, selectedGenres, selectedRoles, selectedPlatforms, sortField, sortDirection]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGenres([]);
    setSelectedRoles([]);
    setSelectedPlatforms([]);
  };

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const toggleRole = (role) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const getEmbedUrl = (song) => {
    const primaryLink = song.links.find(link => link.isPrimary);
    if (primaryLink?.platform === 'YouTube') {
      const videoId = primaryLink.url.split('v=')[1]?.split('&')[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } else if (primaryLink?.platform === 'SoundCloud') {
      return primaryLink.url.replace('/soundcloud.com/', '/w.soundcloud.com/player/?url=https://soundcloud.com/');
    } else if (primaryLink?.platform === 'AudioMack') {
      return primaryLink.url.replace('/audiomack.com/', '/audiomack.com/embed/');
    }
    return null;
  };

  const getYouTubeLink = (song) => {
    return song.links.find(link => link.platform === 'YouTube');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="discography-container">
      <div className="discography-header">
        <h1>Ari Bradshaw Discography</h1>
        <p>Explore over {discographyData.length} songs across various genres and roles</p>
      </div>

      <div className="filters-section">
        <div className="search-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search songs, artists, albums..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear All
            </button>
          </div>
        </div>

        <div className="filter-container">
          <div className="filter-section">
            <h3>Genres</h3>
            <div className="filter-chips">
              {allGenres.map(genre => (
                <button
                  key={genre}
                  onClick={() => toggleGenre(genre)}
                  className={`filter-chip ${selectedGenres.includes(genre) ? 'active' : ''}`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Roles</h3>
            <div className="filter-chips">
              {allRoles.map(role => (
                <button
                  key={role}
                  onClick={() => toggleRole(role)}
                  className={`filter-chip ${selectedRoles.includes(role) ? 'active' : ''}`}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <h3>Platforms</h3>
            <div className="filter-chips">
              {allPlatforms.map(platform => (
                <button
                  key={platform}
                  onClick={() => togglePlatform(platform)}
                  className={`filter-chip ${selectedPlatforms.includes(platform) ? 'active' : ''}`}
                >
                  {platform}
                </button>
              ))}
            </div>
          </div>

          <div className="sort-section">
            <h3>Sort By</h3>
            <div className="sort-controls">
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="sort-select"
              >
                <option value="releaseDate">Release Date</option>
                <option value="title">Title</option>
                <option value="artist">Artist</option>
                <option value="album">Album</option>
              </select>
              <button
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                className="sort-direction-btn"
              >
                {sortDirection === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="results-info">
        <p>Showing {filteredSongs.length} of {discographyData.length} songs</p>
      </div>

      <div className="songs-grid">
        {filteredSongs.map(song => {
          const youtubeLink = getYouTubeLink(song);
          
          return (
            <div key={song.id} className="song-card">
              <div className="song-header">
                <div className="song-info">
                  <h3 className="song-title">{song.title}</h3>
                  <p className="song-artist">{song.artist}</p>
                  {song.album && <p className="song-album">{song.album}</p>}
                  <p className="song-date">{formatDate(song.releaseDate)}</p>
                </div>
                
                {youtubeLink && (
                  <div className="youtube-link">
                    <a
                      href={youtubeLink.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="youtube-btn"
                    >
                      <span className="youtube-icon">▶</span>
                      Watch on YouTube
                    </a>
                  </div>
                )}
              </div>
              
              <div className="song-details">
                <div className="song-tags">
                  {song.genre.map(genre => (
                    <span key={genre} className="tag genre-tag">{genre}</span>
                  ))}
                  {song.roles.map(role => (
                    <span key={role} className="tag role-tag">{role}</span>
                  ))}
                </div>

                <div className="song-links">
                  {song.links.map(link => (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`platform-link ${link.isPrimary ? 'primary' : ''}`}
                    >
                      {link.platform}
                    </a>
                  ))}
                </div>

                {song.description && (
                  <p className="song-description">{song.description}</p>
                )}

                <button
                  onClick={() => setSelectedSong(selectedSong?.id === song.id ? null : song)}
                  className="embed-toggle"
                >
                  {selectedSong?.id === song.id ? 'Hide Player' : 'Show Player'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selectedSong && (
        <div className="embed-modal">
          <div className="embed-content">
            <button 
              onClick={() => setSelectedSong(null)}
              className="close-embed"
            >
              ×
            </button>
            <h3>{selectedSong.title} - {selectedSong.artist}</h3>
            {getEmbedUrl(selectedSong) ? (
              <div className="embed-container">
                {selectedSong.links.find(link => link.isPrimary)?.platform === 'YouTube' ? (
                  <iframe
                    src={getEmbedUrl(selectedSong)}
                    title={`${selectedSong.title} by ${selectedSong.artist}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <iframe
                    src={getEmbedUrl(selectedSong)}
                    title={`${selectedSong.title} by ${selectedSong.artist}`}
                    frameBorder="0"
                    width="100%"
                    height="166"
                    scrolling="no"
                  />
                )}
              </div>
            ) : (
              <p>No embed available for this track.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Discography; 